import { MerchantOrderStatusEnum } from '@/graphql/generatedTypes';

/**
 * Variant for status display - determines label and styling
 * - 'client': Used in OrderForm (customer-facing) - shows "Submitted" for pending
 * - 'admin': Used in EditEventMerchantOrder (admin-facing) - shows "Pending Approval" for pending
 */
export type StatusVariant = 'client' | 'admin';

/**
 * Status preset key - used to map multiple MerchantOrderStatusEnum values to a single visual style
 */
export type StatusPresetKey =
  | 'delivered'
  | 'picked'
  | 'approved'
  | 'pending_approval'
  | 'pending_approval_admin'
  | 'open'
  | 'canceled'
  | 'rejected'
  | 'expired';

/**
 * Visual configuration for a status preset
 */
export type StatusPreset = {
  labelKey: string;
  iconName: string;
  className: string;
};

/**
 * Status presets define the visual appearance for different status groups
 */
export const StatusPresets: Record<StatusPresetKey, StatusPreset> = {
  delivered: {
    labelKey: 'EventOrder:statusDelivered',
    iconName: 'truck',
    className: 'tw:bg-neutral-50 tw:text-black',
  },
  picked: {
    labelKey: 'EventOrder:statusPicked',
    iconName: 'clipboard-container-check',
    className: 'tw:bg-neutral-50 tw:text-black',
  },
  approved: {
    labelKey: 'EventOrder:statusApproved',
    iconName: 'checkmark-circle',
    className: 'tw:bg-neutral-50 tw:text-black',
  },
  // Client view: "Submitted" with green and checkmark
  pending_approval: {
    labelKey: 'EventOrder:statusSubmitted',
    iconName: 'checkmark-circle',
    className: 'tw:bg-success-main tw:text-white',
  },
  // Admin view: "Pending Approval" with yellow/amber and clock
  pending_approval_admin: {
    labelKey: 'EventOrder:statusPendingApproval',
    iconName: 'clock',
    className: 'tw:bg-warning-main tw:text-white',
  },
  open: {
    labelKey: 'EventOrder:statusOpen',
    iconName: 'pencil-edit-frame',
    className: 'tw:bg-primary-main tw:text-white',
  },
  canceled: {
    labelKey: 'EventOrder:statusCanceled',
    iconName: 'trash-delete',
    className: 'tw:bg-neutral-50 tw:text-neutral-400',
  },
  rejected: {
    labelKey: 'EventOrder:statusRejected',
    iconName: 'minus-circle-rejected',
    className: 'tw:bg-neutral-50 tw:text-black',
  },
  // Client-side only
  expired: {
    labelKey: 'EventOrder:statusExpired',
    iconName: 'trash-delete',
    className: 'tw:bg-neutral-50 tw:text-black',
  },
};

/**
 * Maps MerchantOrderStatusEnum to a StatusPresetKey for visual styling
 */
export const PresetKeyByOrderStatus: Record<
  MerchantOrderStatusEnum,
  StatusPresetKey
> = {
  [MerchantOrderStatusEnum.Picked]: 'picked',
  [MerchantOrderStatusEnum.Approved]: 'approved',
  [MerchantOrderStatusEnum.SalesOrderSent]: 'approved',
  [MerchantOrderStatusEnum.Delivered]: 'delivered',
  [MerchantOrderStatusEnum.OrderReceived]: 'pending_approval',
  [MerchantOrderStatusEnum.Canceled]: 'canceled',
  [MerchantOrderStatusEnum.Invalid]: 'canceled',
  [MerchantOrderStatusEnum.OnHold]: 'open',
  [MerchantOrderStatusEnum.PendingApproval]: 'pending_approval',
  [MerchantOrderStatusEnum.Error]: 'canceled',
  // Only reachable for open-system orders (transient SendCloud label failure),
  // styled like Error for the rare case it surfaces here.
  [MerchantOrderStatusEnum.ErrorRetryable]: 'canceled',
  // Open-system only (a carrier returned the parcels). Styled like Error: it is
  // a shipment ops has to act on, not progress.
  [MerchantOrderStatusEnum.ReturnToSender]: 'canceled',
  [MerchantOrderStatusEnum.InTransit]: 'approved',
  [MerchantOrderStatusEnum.SentToPicking]: 'approved',
  // Irrelevant
  [MerchantOrderStatusEnum.CreatedForReview]: 'pending_approval',
  [MerchantOrderStatusEnum.CreatedForReviewPickshare]: 'pending_approval',
  [MerchantOrderStatusEnum.ApprovedForPackageDelivery]: 'approved',
  [MerchantOrderStatusEnum.ApprovedForLocalDelivery]: 'approved',
  [MerchantOrderStatusEnum.AllPrinted]: 'approved',
  [MerchantOrderStatusEnum.PlannedOnTour]: 'approved',
  [MerchantOrderStatusEnum.ReadyToSendByPartner]: 'approved',
  [MerchantOrderStatusEnum.SentByPartner]: 'approved',
  [MerchantOrderStatusEnum.Completed]: 'approved',
  [MerchantOrderStatusEnum.Deleted]: 'canceled',
  [MerchantOrderStatusEnum.Rejected]: 'canceled',
  [MerchantOrderStatusEnum.DpdError]: 'canceled',
  [MerchantOrderStatusEnum.ApprovedSalesOrderSent]: 'approved',
  [MerchantOrderStatusEnum.PickshareOrderCreated]: 'approved',
  [MerchantOrderStatusEnum.ApprovedForPickshareDelivery]: 'approved',
};

/**
 * Get the preset key for a given status, optionally adjusted for admin variant
 */
export function getPresetKeyForStatus(
  status: MerchantOrderStatusEnum,
  variant: StatusVariant = 'client',
): StatusPresetKey {
  const basePresetKey = PresetKeyByOrderStatus[status];
  // For admin variant, use the admin-specific pending_approval preset
  if (variant === 'admin' && basePresetKey === 'pending_approval') {
    return 'pending_approval_admin';
  }
  return basePresetKey;
}

/**
 * Get the status preset for a given MerchantOrderStatusEnum
 */
export function getStatusPreset(
  status: MerchantOrderStatusEnum,
  variant: StatusVariant = 'client',
): StatusPreset {
  const presetKey = getPresetKeyForStatus(status, variant);
  return StatusPresets[presetKey];
}

/**
 * All allowed statuses for event merchant orders in the admin edit page
 * ApprovedSalesOrderSent is always shown but may be disabled
 */
export const EVENT_ORDER_ALLOWED_STATUSES: MerchantOrderStatusEnum[] = [
  MerchantOrderStatusEnum.OrderReceived,
  MerchantOrderStatusEnum.Approved,
  MerchantOrderStatusEnum.SalesOrderSent,
  MerchantOrderStatusEnum.Picked,
  MerchantOrderStatusEnum.Delivered,
  MerchantOrderStatusEnum.Canceled,
];

/**
 * Statuses an operator may manually set on an event order via the status select.
 * Terminal/system-driven states (PICKED, IN_TRANSIT, DELIVERED, ERROR) are
 * intentionally excluded — they are reached by the picking/delivery flow, not by
 * an admin, so they remain display-only. The select still renders such a value
 * as the current status; it just isn't offered as a selectable option.
 */
export const EVENT_ORDER_SETTABLE_STATUSES: MerchantOrderStatusEnum[] = [
  MerchantOrderStatusEnum.OrderReceived,
  MerchantOrderStatusEnum.Approved,
  MerchantOrderStatusEnum.SalesOrderSent,
  MerchantOrderStatusEnum.Canceled,
];

/**
 * Get statuses that should be disabled (visible but not selectable) based on current status
 * ApprovedSalesOrderSent can only be selected if current status is already ApprovedSalesOrderSent
 */
export function getEventOrderDisabledStatuses(
  currentStatus?: MerchantOrderStatusEnum | null,
): MerchantOrderStatusEnum[] {
  // ApprovedSalesOrderSent can only be selected if current status is already that
  if (currentStatus !== MerchantOrderStatusEnum.SalesOrderSent) {
    return [MerchantOrderStatusEnum.SalesOrderSent];
  }
  return [];
}

/**
 * Translation keys for MerchantOrderStatusEnum values (client view)
 * Used for displaying status labels in dropdowns
 */
export const StatusLabelKeys: Partial<Record<MerchantOrderStatusEnum, string>> =
  {
    [MerchantOrderStatusEnum.OrderReceived]: 'EventOrder:statusSubmitted',
    [MerchantOrderStatusEnum.Approved]: 'EventOrder:statusApproved',
    [MerchantOrderStatusEnum.SalesOrderSent]:
      'EventOrder:statusApprovedSalesOrderSent',
    [MerchantOrderStatusEnum.SentToPicking]: 'EventOrder:statusSentToPicking',
    [MerchantOrderStatusEnum.Picked]: 'EventOrder:statusPicked',
    [MerchantOrderStatusEnum.InTransit]: 'EventOrder:statusInTransit',
    [MerchantOrderStatusEnum.Delivered]: 'EventOrder:statusDelivered',
    [MerchantOrderStatusEnum.OnHold]: 'EventOrder:statusOnHold',
    [MerchantOrderStatusEnum.Canceled]: 'EventOrder:statusCanceled',
    [MerchantOrderStatusEnum.Error]: 'EventOrder:statusError',
    [MerchantOrderStatusEnum.Invalid]: 'EventOrder:statusInvalid',
  };

/**
 * Translation keys for MerchantOrderStatusEnum values (admin view)
 * Shows "Pending Approval" instead of "Submitted" for CreatedForReview
 */
export const AdminStatusLabelKeys: Partial<
  Record<MerchantOrderStatusEnum, string>
> = {
  [MerchantOrderStatusEnum.OrderReceived]: 'EventOrder:statusPendingApproval',
  [MerchantOrderStatusEnum.Approved]: 'EventOrder:statusApproved',
  [MerchantOrderStatusEnum.SalesOrderSent]:
    'EventOrder:statusApprovedSalesOrderSent',
  [MerchantOrderStatusEnum.SentToPicking]: 'EventOrder:statusSentToPicking',
  [MerchantOrderStatusEnum.Picked]: 'EventOrder:statusPicked',
  [MerchantOrderStatusEnum.InTransit]: 'EventOrder:statusInTransit',
  [MerchantOrderStatusEnum.Delivered]: 'EventOrder:statusDelivered',
  [MerchantOrderStatusEnum.OnHold]: 'EventOrder:statusOnHold',
  [MerchantOrderStatusEnum.Canceled]: 'EventOrder:statusCanceled',
  [MerchantOrderStatusEnum.Error]: 'EventOrder:statusError',
  [MerchantOrderStatusEnum.Invalid]: 'EventOrder:statusInvalid',
};

/**
 * Get the appropriate label keys based on variant
 */
export function getStatusLabelKeys(
  variant: StatusVariant = 'client',
): Partial<Record<MerchantOrderStatusEnum, string>> {
  return variant === 'admin' ? AdminStatusLabelKeys : StatusLabelKeys;
}
